<template>
	<div
		class="list-tile"
		:class="[{
			'is-mobile': isMobileWebApp,
			'is-playing': isPlaying && !previewFinished,
			'is-tagging-tool': isTaggingToolMode || isMomentsMode,
			'-reduce-vertical-margin': reduceVerticalMargin,
		}, tileMarkingColor]"
		@click="handleClick"
	>
		<div class="content">
			<div
				class="shadow -desktop-hover-only"
			/>
			<div
				class="list-tile--leading"
				:class="{ rounded: roundedLeading }"
			>
				<slot name="leading">
					<div
						v-if="!hideLeading"
						class="placeholder"
						:class="{ rounded: roundedLeading }"
					>
						<slot name="leading-placeholder">
							<span
								v-if="clip.gameMinute"
							>
								<div
									v-if="showClockIcon"
									class="clock-icon"
								>
									<Icon
										icon-name="clock"
										size="sm"
									/>
								</div>
								<div class="game-minute">{{ clip.gameTime.gameMinute }}’</div>
							</span>
							<time
								v-else
								class="duration game-timestamp"
							>{{ clipFormattedTime }}</time>
						</slot>
					</div>
					<div
						class="play-icon"
						:class="{ rounded: roundedLeading }"
					>
						<Icon
							icon-name="button-play-filled"
							:size="roundedLeading ? 'md' : 'lg'"
						/>
					</div>
				</slot>
			</div>
			<div class="list-tile--body">
				<div class="title-wrapper">
					<LabelWithTooltip
						class="top"
						ref-element="title"
						:label="titleComp"
					>
						<h3
							ref="title"
							:class="{'one-line': oneLineTitle}"
							class="title"
						>
							{{ titleComp }}
							<template v-if="currentScore">
								<span class="current-score">{{ currentScore }}</span>
							</template>
						</h3>
					</LabelWithTooltip>
				</div>
				<div
					v-if="teamLogo || teamName || playerName || clipDuration || isHighlightsMode"
					class="subtitle"
				>
					<slot name="subtitle">
						<img
							v-if="teamLogo"
							:src="teamLogo"
							:alt="teamName"
							class="logo"
						>
						<span v-else-if="teamName">{{ teamName }}</span>
						<template v-if="teamName && playerName">
							<span class="divider">|</span>
							<span class="player-name">{{ playerName }}</span>
						</template>
						<span
							v-if="!teamName && playerName"
							class="player-name"
						>
							{{ playerName }}
						</span>

						<template v-if="showGameTimeDisplay">
							<span
								v-if="teamName || playerName"
								class="divider"
							>|</span>
							<span class="time">
								{{ gameTimeDisplay }}
							</span>
						</template>
						<template v-if="clipDuration">
							<span
								v-if="teamName || playerName || showGameTimeDisplay"
								class="divider"
							>|</span>
							<span class="time">
								{{ clipFormattedDuration }}
							</span>
						</template>
						<template v-if="score && isHighlightsMode">
							<span
								v-if="teamName || playerName || clipDuration"
								class="divider"
							>|</span>
							<LabelWithTooltip
								:class="{ '-highlight': aiAnalysis }"
								:label="aiAnalysis ? aiAnalysis.explanation : ''"
								class="iq-score"
							>
								Score: {{ score }}
								<Icon
									class="iq-score__icon"
									icon-name="analytics"
									size="sm"
								/>
							</LabelWithTooltip>
						</template>
					</slot>
				</div>
			</div>
			<div class="list-tile--trailing">
				<ContextMenu
					v-if="showContextMenu"
					:key="`context_menu${clip.id}`"
					:menu-items="contextMenuItems"
					:launcher="menuLauncher"
					@openStatusChanged="handleContextMenuOpenClose"
				>
					<template #toggle>
						<span class="sc-white">
							<Icon
								icon-name="context-menu"
								size="lg"
							/>
						</span>
					</template>
				</ContextMenu>
				<template v-if="isTaggingToolMode && clip.name === 'scene'">
					{{ clip.data.loudnessRating }}
				</template>
				<template v-if="isTaggingToolMode && !hideRating">
					<Rating
						v-if="canEdit"
						class="list-tile__rating"
						:value="clip.rating"
						size="sm"
						@change="updateRating"
					/>
					<RatingView
						class="list-tile__rating-view"
						:rating="clip.rating"
					/>
				</template>
				<Icon
					v-if="!showContextMenu && !hideShare"
					class="share-icon -desktop-hover-only"
					icon-name="network-communication"
					size="lg"
					@click.stop="handleShare()"
				/>
				<LikeButton
					v-if="!showContextMenu && !hideLikes"
					class="clip-like"
					:likes="clip.likes"
					:is-liked="isLikedByUser"
					@like="handleLike"
				/>
				<slot name="trailing" />
			</div>
		</div>
		<div
			class="bottom"
			:class="{indented}"
		>
			<div class="clip-progress-bar">
				<div
					v-if="isPlaying && !previewFinished"
					class="clip-cursor"
					:style="playingCursorStyle"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import EventTag from '@videotools/js/api/entities/EventTag';
import LikeButton from '@shared/components/LikeButton';
import Icon from '@components/Icon';
import ContextMenu from '@components/context-menu/ContextMenu';
import { SportName } from '@shared/enums/SportName';
import Rating from '@videotools/js/one/components/Rating/Rating';
import RatingView from '@videotools/js/one/components/Rating/RatingView';
import MOBILE_MENU_TYPES from '@components/context-menu/constants';
import { mapActions, mapGetters } from 'vuex';
import { bus } from '@videotools/js/events';
import { PLAYER } from '@videotools/js/events/enum';
import { VideoCreateZeroDurationError } from '@videotools/js/store/modules/video-create';
import { showVideoCreateErrorToast } from '@videotools/js/one/components/Video/video-create-utils';
import { TAG_HALFTIME_END, TAG_HALFTIME_START } from '@videotools/js/logic/game/event-tag-intervals';
import laroute from '@laroute';

export default {
	name:       'ClipListTile',
	components: { ContextMenu, LikeButton, Icon, Rating, RatingView },
	props:      {
		clip: {
			type:    EventTag,
			default: null,
		},
		title: {
			type: String,
		},
		isPlaying: {
			type:    Boolean,
			default: false,
		},
		eventId: {
			default: null,
		},
		roundedLeading: {
			type: Boolean,
		},
		indented: {
			type: Boolean,
		},
		hideLeading: {
			type: Boolean,
		},
		hideLikes: {
			type:    Boolean,
			default: false,
		},
		hideShare: {
			type:    Boolean,
			default: false,
		},
		hideRating: {
			type:    Boolean,
			default: false,
		},
		hideContextMenu: {
			type:    Boolean,
			default: false,
		},
		reduceVerticalMargin: {
			type:    Boolean,
			default: true,
		},
		oneLineTitle: {
			type: Boolean,
		},
		canEdit: {
			type: Boolean,
		},
	},
	data() {
		return {
			isContextMenuOpened: false,
			playPosition:        0,
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp:        'app/isMobileWebApp',
			isTaggingToolMode:     'app/isTaggingToolMode',
			isMomentsMode:         'app/isMomentsMode',
			event:                 'event/event',
			sportName:             'event/sportName',
			isDownloadable:        'event/isDownloadable',
			canUseMultiFormatTool: 'permissions/canUseMultiFormatTool',
			isAdMode:              'player/isAdMode',
			clipIsLiked:           'clipLikes/isLiked',
			isHighlightsMode:      'highlights/isHighlightsMode',
		}),
		isLikedByUser() {
			return this.clipIsLiked(this.eventId, this.clip.id);
		},
		showClockIcon() {
			return [TAG_HALFTIME_START, TAG_HALFTIME_END].includes(this.clip.name);
		},
		teamName() {
			return this.clip?.team?.club?.code;
		},
		teamLogo() {
			return this.clip.team?.imageUrl;
		},
		playerName() {
			return this.clip.player?.name;
		},
		score() {
			return this.clip.score;
		},
		aiAnalysis() {
			return this.clip.data.analysis ? this.clip.data.analysis.filter(item => item.highlight)[0] : null;
		},
		clipFormattedDuration() {
			return this.clip.formattedDuration;
		},
		clipDuration() {
			return this.clip.duration;
		},
		clipFormattedTime() {
			return this.clip.formattedTimestamp;
		},
		isIceHockey() {
			return this.sportName === SportName.ICE_HOCKEY;
		},
		displayGameMinute() {
			if (Number.isFinite(this.clip?.gameTimeSeconds) && this.clip.gameTimeSeconds >= 0) {
				return Math.floor(this.clip.gameTimeSeconds / 60) + 1;
			}

			if (Number.isFinite(this.clip?.gameTime?.gameMinute)) {
				return this.clip.gameTime.gameMinute > 0 ? this.clip.gameTime.gameMinute : 1;
			}

			if (Number.isFinite(this.clip?.gameMinute)) {
				return this.clip.gameMinute > 0 ? this.clip.gameMinute : 1;
			}

			return null;
		},
		gameTimeDisplay() {
			if (this.isIceHockey) {
				if (!Number.isFinite(this.clip?.gameTimeSeconds) || this.clip.gameTimeSeconds < 0) {
					return null;
				}

				const minutes = Math.floor(this.clip.gameTimeSeconds / 60);
				const seconds = this.clip.gameTimeSeconds % 60;

				return `${minutes}:${seconds.toString().padStart(2, '0')}`;
			}

			if (!Number.isFinite(this.displayGameMinute)) {
				return null;
			}

			return `${this.displayGameMinute}'`;
		},
		showGameTimeDisplay() {
			return typeof this.gameTimeDisplay === 'string';
		},
		titleComp() {
			if (this.title) {
				return title;
			}
			return this.transTagLabel(this.clip.name, this.sportName, this.clip.tag?.type);
		},
		currentScore() {
			if (!Number.isInteger(this.clip.scoreHome) || !Number.isInteger(this.clip.scoreGuest)) {
				return null;
			}
			return `(${this.clip.scoreHome}:${this.clip.scoreGuest})`;
		},
		previewFinished() {
			return this.playPosition <= this.clip.startTimestamp || this.playPosition >= this.clip.endTimestamp;
		},
		playingCursorStyle() {
			const total = this.clip.endTimestamp - this.clip.startTimestamp;
			if (!total) {
				return { width: 0 };
			}
			const val = this.playPosition - this.clip.startTimestamp;
			const width = 100 * val / total;
			return { width: `${width}%` };
		},
		showContextMenu() {
			return this.isTaggingToolMode && !this.hideContextMenu;
		},
		contextMenuItems() {
			const	actions = [];

			if (this.canEdit) {
				actions.push({
					id:      'edit-action',
					label:   this.$Helpers.ucfirst(this.$I18n.trans('commons.edit')),
					icon:    'pen',
					action:  'edit',
					handler: this.handleEdit,
				});
			}

			if (!this.hideShare) {
				actions.push({
					id:      'share-action',
					label:   this.$Helpers.ucfirst(this.$I18n.trans('commons.share')),
					icon:    'network-communication',
					action:  'share',
					handler: this.handleShare,
				});
			}

			if (this.canUseMultiFormatTool && this.isDownloadable) {
				actions.push({
					id:      'multi-format-action',
					label:   this.$I18n.trans('event.multiformat_editor.title'),
					icon:    'formatconverter',
					action:  'multi-format',
					handler: this.handleMultiFormat,
				});
			}

			if (!this.isMobileWebApp) {
				actions.push({
					id:      'create-video',
					label:   this.$I18n.trans('commons.type_create', { type: this.$I18n.trans_choice('commons.video', 1) }),
					icon:    'video',
					action:  'create-video',
					handler: this.handleCreateVideo,
				});
			}

			if (this.canEdit) {
				actions.push({
					id:      'delete-action',
					label:   this.$Helpers.ucfirst(this.$I18n.trans('commons.delete')),
					icon:    'bin',
					action:  'delete',
					handler: this.handleDelete,
					class:   'danger',
				});
			}

			return actions;
		},
		menuLauncher() {
			if (this.isMobileWebApp) return this.handleContextMenuOpen;
			return null;
		},
		tileMarkingColor() {
			return this.isHighlightsMode && this.clip?.data?.loudnessRating ? ('-score-' + this.clip.scoreIntensity) : '';
		},
	},
	watch: {
		isPlaying(status) {
			if (status) {
				this.subscribePlayerProgress();
			} else {
				this.unsubscribePlayerProgress();
			}
		},
		previewFinished(status) {
			if (status) this.unsubscribePlayerProgress();
		},
	},
	mounted() {
		// If the tile is already active on first render, the watcher won't run yet.
		// Subscribe here so the first clip receives progress updates immediately.
		if (this.isPlaying) {
			this.subscribePlayerProgress();
		}
	},
	destroyed() {
		this.unsubscribePlayerProgress();
	},
	methods: {
		...mapActions({
			deleteEventTags:    'event/deleteEventTags',
			editEventTag:       'eventTagEdit/editEventTag',
			prepareVideoCreate: 'videoCreate/prepare',
			likeClip:           'clipLikes/likeClip',
		}),
		[`BUS-${PLAYER.ON.PROGRESS}`](position) {
			this.playPosition = position;
		},
		handleClick() {
			if (this.isAdMode) {
				return;
			};
			this.subscribePlayerProgress();
			this.$emit('play', this.clip);
		},
		handleEdit() {
			this.$emit('edit', this.clip);
		},
		handleShare() {
			this.$emit('share', this.clip);
		},
		handleRemove() {
			this.$emit('remove', this.clip);
		},
		handleMultiFormat() {
			const route = `${laroute.route('manager.one', {
				event: this.event.id,
			})}/capture?eventTagId=${this.clip.id}`;
			window.open(route);
		},
		handleDelete() {
			this.$emit('delete', this.clip);
		},
		// todo highlight tool: can be removed (edit: not sure about that one... PM wants to keep it)
		async handleCreateVideo() {
			const eventTag = this.clip;
			try {
				await this.prepareVideoCreate([eventTag]);
			} catch (e) {
				if (e instanceof VideoCreateZeroDurationError) {
					showVideoCreateErrorToast(
						[eventTag],
						() => this.handleEdit()
					);
				} else {
					throw e;
				}
			}
		},
		handleContextMenuOpen() {
			bus.publish('context-menu-mobile:open', {
				menuType:    MOBILE_MENU_TYPES.eventTag,
				menuItems:   this.contextMenuItems,
				contextData: this.clip,
			});
		},
		handleContextMenuOpenClose(status) {
			this.isContextMenuOpened = status;
		},
		subscribePlayerProgress() {
			bus.subscribe(PLAYER.ON.PROGRESS, this[`BUS-${PLAYER.ON.PROGRESS}`]);
		},
		unsubscribePlayerProgress() {
			this.playPosition = 0;
			bus.unsubscribe(PLAYER.ON.PROGRESS, this[`BUS-${PLAYER.ON.PROGRESS}`]);
		},
		updateRating(rating) {
			this.$emit('update-rating', { rating, clip: this.clip });
		},
		async handleLike() {
			await this.likeClip({ eventId: this.eventId, clipId: this.clip.id });
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import '@videotools/sass/_fonts';

.list-tile {
	display: flex;
	position: relative;
	flex-direction: column;

	&.-score-1 { border-left: 4px solid rgba(255, 255, 153, 0.6); } /* Light Yellow */
	&.-score-2 { border-left: 4px solid rgba(255, 230, 128, 0.6); }
	&.-score-3 { border-left: 4px solid rgba(255, 204, 102, 0.6); }
	&.-score-4 { border-left: 4px solid rgba(255, 179, 77, 0.7); }
	&.-score-5 { border-left: 4px solid rgba(255, 153, 51, 0.7); }
	&.-score-6 { border-left: 4px solid rgba(255, 128, 26, 0.7); }
	&.-score-7 { border-left: 4px solid rgba(255, 102, 0, 0.75); }
	&.-score-8 { border-left: 4px solid rgba(230, 77, 0, 0.8); }
	&.-score-9 { border-left: 4px solid rgba(204, 51, 0, 0.8); } /* Deep Red */
	&.-score-10 { border-left: 4px solid rgba(179, 0, 0, 0.85); } /* Darkest Red */

	&.-reduce-vertical-margin {
		margin-top: -1px;
	}

	body.-t-light & {
		color: var(--color-black);
	}

	.player-name {
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	body:not(.is-community-cropping) & {
		&.is-tagging-tool {
			padding: 8px 8px 0 16px;

			.list-tile--leading .placeholder {
				background: var(--color-grey-850);
			}

			.bottom .clip-progress-bar {
				background: var(--color-grey-800);
			}

			.list-tile--body .title {
				@include text-md-1;
			}

			&.is-mobile {
				padding: 8px 8px 0 8px;
			}
	}

	}

	.player-name {
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&.is-tagging-tool {
		.list-tile--body .title {
			@include text-md-1;
		}

	}

	.content {
		display: flex;
		height: 64px;
		margin: 8px 8px 0 8px;
	}

	@media(hover:hover) {
		&:hover {
			background: var(--color-grey-950);
			cursor: pointer;

			body.-t-krone & {
				background: var(--color-grey-900);
			}

			&.is-tagging-tool {
				background: var(--color-grey-850);
			}

			&:not(.is-tagging-tool) .shadow.-desktop-hover-only {
				visibility: visible;
				opacity: 1;
			}

			.share-icon.-desktop-hover-only {
				visibility: visible;
				opacity: 1;
			}

			.list-tile--leading {
				.placeholder {
					display: none;
				}
				.play-icon {
					display: flex;
				}
			}
		}
	}

	&:not(.is-mobile) .-desktop-hover-only {
		visibility: hidden;
		opacity: 0;
		transition: opacity 0.3s ease;
	}

	&.is-mobile {
		.list-tile--trailing {
			margin-right: 8px;
		}
	}

	.list-tile--leading {
		height: 64px;
		min-width: 64px;
		display: flex;
		align-items: center;
		justify-content: center;
		@include text-sm-2;

		&.rounded {
			width: 48px;
		}

		.placeholder,.play-icon {
			display: flex;
			width: 100%;
			height: 100%;
			align-items: center;
			justify-content: center;

			&.rounded {
				height: 48px;
				width: 48px;
				border-radius: 50%;
			}
		}

		.placeholder {
			background: var(--color-grey-950);
			border-radius: 2px;
		}

		.play-icon {
			display: none;
			background: var(--color-grey-900);
			border-radius: 2px;

			&.rounded {
				padding-left: 2px;
			}
		}
	}

	&.rounded-leading {
		.list-tile--leading {
			width: 48px;
		}
	}

	&.is-playing {
		.list-tile--leading {
			.placeholder {
				background: var(--color-grey-700);
			}
		}
	}

	.list-tile--body {
		flex-grow: 1;
		min-width: 0;
		display: flex;
		flex-direction: column;
		justify-content: center;
		padding: 0 8px 0 16px;
		gap: 8px;

		.title-wrapper {
			display: flex;
			align-items: center;
		}

		.title {
			@include text-md-1;
			overflow: hidden;
			text-overflow: ellipsis;

			&:not(.one-line) {
				display: -webkit-box; // stylelint-disable-line value-no-vendor-prefix
				-webkit-line-clamp: 2;
				-webkit-box-orient: vertical; // stylelint-disable-line property-no-vendor-prefix
				white-space: normal;
			}
		}

		.current-score {
			color: var(--color-grey-300);
		}

		.divider {
			margin-left: 8px;
			margin-right: 8px;
		}

		.subtitle {
			@include text-sm-1;
			display: flex;
			align-items: center;
			overflow: hidden;
			white-space: nowrap;
			text-overflow: ellipsis;
			color: var(--color-grey-300);
		}

		.logo {
			width: 16px;
			height: 16px;
		}

		.time {
			@include text-sm-1;
		}
	}

	&:not(.is-mobile) {
		.shadow {
			position: absolute;
			right: 0;
			top: 0;
			width: 120px;
			height: 81px;
			background: linear-gradient(90deg, rgba(var(--color-rgb-grey-950), 0) 0%, var(--color-grey-950) 50%);
		}

		.list-tile--trailing .share-icon {
			position: absolute;
			top: 0;
			right: 40px;
			margin-right: 0;
		}
	}

	.clip-like {
		margin-top: 8px;
	}

	.bottom {
		margin-left: 8px;
		margin-right: 8px;

		.clip-progress-bar {
			overflow: hidden;
			margin-top: 7px;
			height: 1px;
			background: var(--color-grey-900);

			.clip-cursor {
				height: 1px;
				background: var(--color-hype-yellow);
				transition: width 0.25s linear;
			}
		}

		&.indented {
			display: flex;
			justify-content: flex-end;
			.clip-progress-bar {
				width: calc(100% - 48px - 16px);
			}
		}
	}
}

.list-tile--trailing {
	display: flex;
	position: relative;

	.list-tile.is-tagging-tool & {
		flex-direction: column;
		align-items: flex-end;
		gap: 10px;
	}

	.share-icon {
		margin-top: 8px;
		margin-right: 17px;
	}

	.context-menu {
		margin-right: 4px;
	}
}

.list-tile__rating {
	display: none;

	@media(hover:hover) {
		.list-tile:hover & {
			display: flex;
		}
	}
}

.list-tile__rating-view {
	display: flex;

	@media(hover:hover) {
		.list-tile:hover & {
			display: none;
		}
	}
}

.game-timestamp {
	color: var(--color-grey-300);
}

.clock-icon {
	display: flex;
	justify-content: center;
	margin-bottom: 6px;
	color: var(--color-white);
}

.game-minute {
	color: var(--color-grey-100);
	display: flex;
	justify-content: center;
}

.iq-score {
	display: flex;

	&.-highlight {
		color: var(--color-hype-yellow);
	}
}

.iq-score__icon {
	margin-left: 4px;
	margin-top: -3px;
}

.remove-icon {
	height: 100%;
}
</style>
